import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import PublicLayout from "@/components/public/PublicLayout";
import { SEOHead } from "@/components/SEOHead";

const INDUSTRIES = ["Alle", "Physiotherapie", "Friseur", "Handwerk", "Restaurant", "Fitness", "Beratung"];

const PORTFOLIO_ITEMS = [
  {
    id: 1,
    name: "PhysioPlus Neustadt",
    industry: "Physiotherapie",
    package: "Business",
    desc: "Modernes Online-Booking-System, Team-Vorstellung, Leistungsübersicht und FAQ — alles auf einer übersichtlichen Website.",
    color: "from-blue-600/25 to-cyan-600/15",
    accent: "#3B82F6",
  },
  {
    id: 2,
    name: "Salon Éclat",
    industry: "Friseur",
    package: "Business",
    desc: "Elegantes Design, Preisliste, Online-Terminbuchung und Galerie — für einen Premium-Friseur in der Innenstadt.",
    color: "from-purple-600/25 to-pink-600/15",
    accent: "#A855F7",
  },
  {
    id: 3,
    name: "Müller Bau GmbH",
    industry: "Handwerk",
    package: "Premium",
    desc: "Vertrauensaufbau durch Referenz-Galerie, Projektanfrage-Wizard und detaillierte Leistungsbeschreibungen.",
    color: "from-orange-600/25 to-amber-600/15",
    accent: "#F97316",
  },
  {
    id: 4,
    name: "Vitality Fitness Club",
    industry: "Fitness",
    package: "Premium",
    desc: "Mitgliedschaft-Tiers, Kursplan, Team und Online-Anmeldung — für ein modernes Fitnessstudio.",
    color: "from-green-600/25 to-emerald-600/15",
    accent: "#22C55E",
  },
  {
    id: 5,
    name: "Trattoria Bella Vista",
    industry: "Restaurant",
    package: "Business",
    desc: "Digitale Speisekarte, Öffnungszeiten, Reservierungs-Widget und atmosphärische Galerie.",
    color: "from-red-600/25 to-rose-600/15",
    accent: "#EF4444",
  },
  {
    id: 6,
    name: "Konsult GmbH",
    industry: "Beratung",
    package: "Premium",
    desc: "Professioneller Unternehmensauftritt, Case-Studies, Team-Blog und Lead-Capture-Formular.",
    color: "from-slate-600/25 to-zinc-600/15",
    accent: "#64748B",
  },
];

export default function PortfolioPage() {
  const [activeFilter, setActiveFilter] = useState("Alle");

  const filtered = activeFilter === "Alle"
    ? PORTFOLIO_ITEMS
    : PORTFOLIO_ITEMS.filter((p) => p.industry === activeFilter);

  return (
    <PublicLayout>
      <SEOHead
        title="Portfolio — Beispiel-Websites"
        description="Entdecken Sie unsere Beispiel-Websites für Physiotherapie, Friseur, Handwerk, Restaurant, Fitness und Beratung — professionell, modern und conversion-optimiert."
        canonical="/portfolio"
      />
      {/* Hero */}
      <section className="pt-32 pb-16 px-6 text-center">
        <div className="max-w-3xl mx-auto">
          <h1
            className="font-black text-5xl sm:text-6xl text-white mb-5 leading-tight"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Beispiel-Websites
          </h1>
          <p className="text-lg text-white/50 leading-relaxed">
            Jede Website ist individuell gestaltet — basierend auf der Branche, dem Stil und den Zielen Ihres Unternehmens.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="px-6 pb-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-2 justify-center">
            {INDUSTRIES.map((ind) => (
              <button
                key={ind}
                onClick={() => setActiveFilter(ind)}
                data-testid={`filter-${ind.toLowerCase()}`}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  activeFilter === ind
                    ? "bg-cyan-400 text-slate-900 shadow-lg shadow-cyan-400/20"
                    : "bg-white/5 text-white/60 hover:bg-white/10 hover:text-white border border-white/5"
                }`}
              >
                {ind}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="px-6 pb-24">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((item) => (
            <div key={item.id} className="group bg-[#0A1220] border border-white/5 rounded-2xl overflow-hidden hover:border-white/15 transition-all">
              {/* Preview */}
              <div className={`aspect-[16/10] bg-gradient-to-br ${item.color} relative overflow-hidden`}>
                {/* Browser chrome */}
                <div className="absolute top-0 left-0 right-0 h-7 bg-black/40 backdrop-blur-sm flex items-center px-3 gap-1.5">
                  <div className="w-2 h-2 rounded-full bg-red-400/60" />
                  <div className="w-2 h-2 rounded-full bg-yellow-400/60" />
                  <div className="w-2 h-2 rounded-full bg-green-400/60" />
                  <div className="ml-2 flex-1 h-3.5 bg-white/5 rounded-sm" />
                </div>
                {/* Content preview simulation */}
                <div className="absolute inset-0 mt-7 p-4 flex flex-col gap-2">
                  <div className="h-6 rounded bg-white/10 w-2/3" style={{ backgroundColor: `${item.accent}20` }} />
                  <div className="h-3 rounded bg-white/5 w-full" />
                  <div className="h-3 rounded bg-white/5 w-4/5" />
                  <div className="flex gap-2 mt-1">
                    <div className="h-8 rounded-lg flex-1 bg-white/5" />
                    <div className="h-8 rounded-lg flex-1 bg-white/5" />
                    <div className="h-8 rounded-lg flex-1 bg-white/5" />
                  </div>
                  <div className="mt-auto flex items-center justify-center">
                    <div
                      className="px-5 py-2 rounded-lg text-xs font-bold text-white/80 opacity-80"
                      style={{ backgroundColor: `${item.accent}40` }}
                    >
                      {item.name}
                    </div>
                  </div>
                </div>
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <Link
                    to="/kontakt#demo-form"
                    className="flex items-center gap-2 px-5 py-2.5 bg-cyan-400 text-slate-900 text-sm font-bold rounded-lg hover:bg-cyan-300 transition-all shadow-xl"
                  >
                    Ähnliche Website anfragen <ArrowRight size={14} />
                  </Link>
                </div>
              </div>

              {/* Info */}
              <div className="p-5">
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div>
                    <p className="font-semibold text-white">{item.name}</p>
                    <p className="text-xs text-white/40 mt-0.5">{item.industry}</p>
                  </div>
                  <span className="text-xs bg-white/5 text-white/50 px-2.5 py-1 rounded-full border border-white/5 whitespace-nowrap">
                    {item.package}
                  </span>
                </div>
                <p className="text-xs text-white/45 leading-relaxed">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#070d1a]">
        <div className="max-w-2xl mx-auto text-center">
          <h2
            className="font-black text-4xl text-white mb-4"
            style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
          >
            Ihre Branche dabei?
          </h2>
          <p className="text-white/50 mb-8">
            Wir erstellen für fast jede Branche professionelle Websites. Sprechen Sie uns einfach an.
          </p>
          <Link
            to="/kontakt#demo-form"
            className="inline-flex items-center gap-2 px-8 py-4 bg-cyan-400 text-slate-900 font-bold rounded-xl hover:bg-cyan-300 transition-all shadow-xl shadow-cyan-400/20"
          >
            Demo anfragen <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </PublicLayout>
  );
}
